import { Side } from '@/compiled_proto/com/celertech/marketdata/api/enums/SideProto';
import { TimeInForceType } from '@/compiled_proto/com/celertech/positionmanager/api/enums/TimeInForceTypeProto';
import { calculateVWAP } from '@/helpers/orderbookHelper';
import { useAppSelector } from '@/state/hooks';
import { PairMap } from '@/state/reducers/marketPairSlice';
import { useCurrency, UseCurrencyReturn } from '@/utils/hooks/useCurrency';
import { useInstrument } from '@/utils/hooks/useInstrument';
import { PriceLevel } from '@/utils/pricebook';
import BigNumber from 'bignumber.js';
import { useMemo } from 'react';
import { useNumberFormatter } from 'react-aria';
import { useFormContext } from 'react-hook-form';

export interface UseTotalsReturn {
    bids: PriceLevel[] | null;
    asks: PriceLevel[] | null;
    vwap: BigNumber | null;
    total: string;
    totalValue: number;
    ccyConfig: UseCurrencyReturn;
    bidAsk: { bid: number; ask: number; timestamp: number; latency: string };
    message: string | null;
    matchType: 'bid' | 'ask';
    disableSubmit: boolean;
    formatPrice: (price: number) => string;
}

export interface UseTotalsProps {
    form: ReturnType<typeof useFormContext<any>>;
    name?: string;
    pairMap: PairMap;
    side: Side;
    ccy1: string;
    ccy2: string;
    ccy2Order: boolean;
    bids: PriceLevel[] | null;
    asks: PriceLevel[] | null;
}

const useTotals = (props: UseTotalsProps): UseTotalsReturn => {
    const { form, name = 'quantity', pairMap, side, ccy1, ccy2, ccy2Order, bids, asks } = props;

    const ccy1Config = useCurrency(ccy1);
    const ccy2Config = useCurrency(ccy2);

    const bidAsk = useAppSelector((state) => state.marketPair.bidAsk[pairMap?.celer || '']);

    const { watch, getFieldState, formState } = form;

    const [quantity, duration] = watch([name, 'duration']);
    const quantityState = getFieldState(name, formState);

    const { formatPrice } = useInstrument(pairMap?.celer);
    const { order_decimals } = useCurrency(ccy2Order ? ccy1 : ccy2);

    const totalFormatter = useNumberFormatter({
        minimumFractionDigits: order_decimals,
        maximumFractionDigits: order_decimals
    });

    const orderQuantity = useMemo(() => +quantity, [quantity]);
    const ccy2ConvertedQuantity = useMemo(() => {
        if (ccy2Order) {
            if (side === Side.BUY && asks) {
                return BigNumber(+quantity)
                    .dividedBy(asks[asks.length - 1]?.price || 1)
                    .toNumber();
            } else if (side === Side.SELL && bids) {
                return BigNumber(+quantity)
                    .dividedBy(bids[0]?.price || 1)
                    .toNumber();
            }
            return 0;
        } else return +quantity;
    }, [quantity]);

    const matchType = useMemo(() => {
        if (ccy2Order) return side === Side.BUY ? 'bid' : 'ask';
        else return side === Side.BUY ? 'ask' : 'bid';
    }, [side, ccy2Order]);

    const { vwap, message, disableSubmit } = useMemo(() => {
        if (orderQuantity && !quantityState.invalid) {
            if ((side === Side.BUY && !ccy2Order) || (side === Side.SELL && ccy2Order)) {
                // use asks
                const reversedAsks = asks ? [...asks].reverse() : [];
                if (duration.value !== TimeInForceType.FOK) {
                    return calculateVWAP(reversedAsks, ccy2ConvertedQuantity);
                } else {
                    const availableAsks = reversedAsks.filter((ask) => ask.quantity >= quantity);
                    const ask = availableAsks.length > 0 ? availableAsks[0] : null;

                    return {
                        vwap: ask ? BigNumber(ask.price) : null,
                        message: ask ? null : 'No available ask price for the specified quantity',
                        disableSubmit: ask ? false : true
                    };
                }
            } else if ((side === Side.SELL && !ccy2Order) || (side === Side.BUY && ccy2Order)) {
                // use bids
                if (duration.value !== TimeInForceType.FOK) {
                    return calculateVWAP(bids, ccy2ConvertedQuantity);
                } else {
                    const availableBids = bids?.filter((bid) => bid.quantity >= quantity) || [];
                    const bid = availableBids.length > 0 ? availableBids[0] : null;

                    return {
                        vwap: bid ? BigNumber(bid.price) : null,
                        message: bid ? null : 'No available bid price for the specified quantity',
                        disableSubmit: bid ? false : true
                    };
                }
            } else {
                return { vwap: null, message: null, disableSubmit: true };
            }
        } else {
            return { vwap: null, message: null, disableSubmit: true };
        }
    }, [bids, asks, quantity, quantityState, duration]);

    const { total, totalValue, ccyConfig } = useMemo(() => {
        if (ccy2Order) {
            const totalValue = vwap
                ? BigNumber(orderQuantity)
                      .dividedBy(vwap || 1)
                      .toNumber()
                : 0;

            return {
                total: vwap ? `${totalFormatter.format(totalValue)} ${ccy1Config.show}` : '-',
                totalValue,
                ccyConfig: ccy1Config
            };
        } else {
            const totalValue = vwap ? BigNumber(orderQuantity).multipliedBy(vwap).toNumber() : 0;

            return {
                total: vwap ? `${totalFormatter.format(totalValue)} ${ccy2Config.show}` : '-',
                totalValue,
                ccyConfig: ccy2Config
            };
        }
    }, [vwap, ccy2Order, side, orderQuantity]);

    return {
        bids,
        asks,
        vwap,
        total,
        totalValue,
        ccyConfig,
        bidAsk,
        message,
        matchType,
        disableSubmit,
        formatPrice
    };
};

export default useTotals;
